import { useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/feature/ScrollToTopButton';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, categories } from '@/mocks/articles';

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || categories[0].key;

  const filtered = useMemo(
    () => articles.filter((a) => a.category === activeTab),
    [activeTab],
  );

  const switchTab = (tab: string) => {
    setSearchParams({ tab });
  };

  return (
    <>
      <Navbar />
      <ScrollToTop />
      <main>
        {/* Page Header */}
        <div className="bg-foreground-950 pt-24 pb-10 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: activeTab },
              ]}
            />
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-background-50 mt-4">
              缘之空{activeTab}
            </h1>
            <p className="text-foreground-400 mt-2 text-base">
              深度探索缘之空的{activeTab}相关文章，全方位了解这部经典视觉小说
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="border-b border-background-200 bg-background-50 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 -mb-px overflow-x-auto" role="tablist">
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => switchTab(cat.key)}
                  role="tab"
                  aria-selected={activeTab === cat.key}
                  className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors cursor-pointer ${
                    activeTab === cat.key
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === cat.key ? 'bg-primary-100 text-primary-600' : 'bg-background-200 text-foreground-400'
                  }`}>
                    {articles.filter((a) => a.category === cat.key).length}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Article Grid */}
        <section className="py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop="">
                {filtered.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-file-search-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章，敬请期待</p>
              </div>
            )}

            {/* Related tags */}
            {filtered.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200">
                <h3 className="text-sm font-semibold text-foreground-900 mb-3">相关标签</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  {Array.from(new Set(filtered.flatMap((a) => a.tags))).map((tag) => (
                    <Link
                      key={tag}
                      to={`/category?tab=${activeTab}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 transition-colors"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTopButton />
    </>
  );
}